/* eslint-disable no-nested-ternary */
import { MultiToggle } from '@tmov/design-system';
import { SpinnerMedium } from '@tmov/design-system/icons';
import { Tokens } from '@tmov/design-system/theme/types';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { LoadingContainer } from '../../../../components/LoadingContainer';
import {
  formatarMilhar,
  formatarMoedaIMask,
  formatarPorcentagemAderencia,
} from '../../../../helpers/formatador';
import { groupPeriodoByProp } from '../helpers/groupPeriodoByProp';
import useMedidaPeriodoData from '../hooks/useMedidaPeriodoData';
import { Container, MedidaHeader } from './styles';

const timeFrames = [
  { id: 'mes', value: 'Mês' },
  { id: 'ano', value: 'Ano' },
];

const TimeGraph = ({ clientes, filters }) => {
  const { medidaPeriodoData, loadingMedidaPeriodoData } = useMedidaPeriodoData({
    filters,
    clientes,
  });

  const [selectedTimeFrame, setSelectedTimeFrame] = useState({
    id: 'ano',
    value: 'Ano',
  });

  const data = useMemo(() => {
    const shipperData = medidaPeriodoData.filter(
      item => item.flag_lote_shipper === 'True'
    );
    const groupedByPeriodoData = groupPeriodoByProp(
      shipperData,
      selectedTimeFrame.id
    );
    const sortedByDateData = groupedByPeriodoData.sort(
      (a, b) => a.ano - b.ano || a.mes - b.mes
    );
    return sortedByDateData;
  }, [medidaPeriodoData, selectedTimeFrame.id]);

  const formatterGraphValue = (value, name) => {
    if (value && name) {
      switch (name) {
        case 'quantidade_viagens':
          return value;
        case 'ticket_medio':
          return formatarMoedaIMask(value);
        case 'aderencia_cadencia':
          return formatarPorcentagemAderencia(value);
        default:
          return formatarMilhar(value);
      }
    }

    return value;
  };

  const formatterGraphName = name => {
    if (name) {
      switch (name) {
        case 'peso_volume':
          return 'Volume (ton)';
        case 'valor_custo':
          return 'Faturamento (R$)';
        case 'quantidade_viagens':
          return 'Quantidade de viagens';
        case 'ticket_medio':
          return 'Ticket Médio (R$/ton)';
        default:
          return 'Aderência (%)';
      }
    }
    return name;
  };

  return (
    <Container>
      <MedidaHeader>
        <h3>{filters?.medida?.value}</h3>
        <MultiToggle
          selectedOption={selectedTimeFrame}
          options={timeFrames}
          onChange={option => setSelectedTimeFrame(option)}
        />
      </MedidaHeader>
      {data?.length > 0 ? (
        <ResponsiveContainer width="100%" height={184}>
          <AreaChart data={data}>
            <XAxis dataKey="timeFrame" />
            <YAxis dataKey={filters?.medida?.id} tick={false} />
            <Tooltip
              labelFormatter={value => `${selectedTimeFrame.value}: ${value}`}
              formatter={(value, name, props) => [
                formatterGraphValue(value, name),
                formatterGraphName(name),
              ]}
            />
            <Area
              dataKey={filters?.medida?.id}
              stroke={Tokens.eixods.colors.brand.primary[80]}
              fill={Tokens.eixods.colors.brand.primary[5]}
            />
          </AreaChart>
        </ResponsiveContainer>
      ) : loadingMedidaPeriodoData ? (
        <LoadingContainer>
          <SpinnerMedium />
        </LoadingContainer>
      ) : (
        <h3>Não há dados de período para os filtros definidos</h3>
      )}
    </Container>
  );
};

TimeGraph.propTypes = {
  clientes: PropTypes.arrayOf(PropTypes.string).isRequired,
  filters: PropTypes.shape({
    periodo: PropTypes.arrayOf(PropTypes.object),
    handlePeriodo: PropTypes.func,
    produto: PropTypes.shape({
      id: PropTypes.number,
      value: PropTypes.string,
    }),
    handleProduto: PropTypes.func,
    cidade: PropTypes.shape({
      id: PropTypes.number,
      value: PropTypes.string,
    }),
    handleCidade: PropTypes.func,
    estado: PropTypes.shape({
      id: PropTypes.number,
      value: PropTypes.string,
    }),
    handleEstado: PropTypes.func,
    medida: PropTypes.shape({
      id: PropTypes.string,
      value: PropTypes.string,
    }),
    handleMedida: PropTypes.func,
  }).isRequired,
};

export default TimeGraph;
